import { useEffect, useState } from "react";
import {
  DEFAULT_STORE_ENTRY,
  type StoreEntryFlow,
  type PublicIntakeProgram,
} from "@bloom/shared";
import api from "../../lib/api";
export function StoreEntryFlowEditor() {
  const [flow, setFlow] = useState<StoreEntryFlow>(DEFAULT_STORE_ENTRY);
  const [programs, setPrograms] = useState<PublicIntakeProgram[]>([]);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    api
      .get<any>("/users/me/custom-store/entry-flow")
      .then((res) => {
        setFlow(res.data.flow);
        setPrograms(res.data.programs);
        setReady(true);
      })
      .catch(() => setError("Unable to load patient entry settings."));
  }, []);
  const patch = (changes: Partial<StoreEntryFlow>) => {
    setFlow((f) => ({ ...f, ...changes }));
    setMessage("");
  };
  return (
    <section className="mt-6 rounded-xl border bg-white p-6 space-y-5">
      <div>
        <h2 className="font-semibold text-gray-900">Patient entry flow</h2>
        <p className="mt-1 text-sm text-gray-500">
          Choose how patients begin. Admins manage the program rules, eligible
          doses, and required questions. Saving applies to your live store.
        </p>
      </div>
      {error && (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}
      {message && (
        <p role="status" className="text-sm text-green-700">
          {message}
        </p>
      )}
      <fieldset disabled={!ready || busy} className="space-y-4">
        <label className="block text-sm">
          Entry options
          <select
            className="mt-1 w-full border rounded-lg p-2"
            value={flow.mode}
            onChange={(e) =>
              patch({ mode: e.target.value as StoreEntryFlow["mode"] })
            }
          >
            <option value="direct">
              Direct selection — choose a treatment, then required intake
            </option>
            <option value="guided">
              Guided — recommend a program from an assessment
            </option>
            <option value="both">Offer both paths</option>
          </select>
        </label>
        {flow.mode !== "direct" && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Guided programs</h3>
            {!programs.length && (
              <p className="text-sm text-gray-500">
                An admin must configure an active program containing products
                available to your clinic.
              </p>
            )}
            {programs.map((p) => (
              <label key={p.id} className="flex gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={flow.programIds.includes(p.id)}
                  onChange={(e) =>
                    patch({
                      programIds: e.target.checked
                        ? [...flow.programIds, p.id]
                        : flow.programIds.filter((id) => id !== p.id),
                    })
                  }
                />
                {p.name}
              </label>
            ))}
          </div>
        )}
        {(["directLabel", "guidedLabel", "introduction"] as const).map(
          (key) => (
            <label key={key} className="block text-sm">
              {
                {
                  directLabel: "Direct button label",
                  guidedLabel: "Guided button label",
                  introduction: "Introduction",
                }[key]
              }
              <input
                className="mt-1 w-full border rounded-lg p-2"
                maxLength={key === "introduction" ? 500 : 60}
                value={flow[key]}
                onChange={(e) => patch({ [key]: e.target.value })}
              />
            </label>
          ),
        )}
        <button
          className="rounded-lg bg-blue-600 text-white px-4 py-2 disabled:opacity-50"
          onClick={async () => {
            setBusy(true);
            setError("");
            setMessage("");
            try {
              const res: any = await api.put(
                "/users/me/custom-store/entry-flow",
                flow,
              );
              if (!res.success) throw new Error(res.error);
              setMessage("Patient entry flow saved.");
            } catch (e: any) {
              setError(
                e.response?.data?.error?.message ||
                  e.response?.data?.error ||
                  e.message ||
                  "Unable to save",
              );
            } finally {
              setBusy(false);
            }
          }}
        >
          Save entry flow
        </button>
      </fieldset>
    </section>
  );
}
